import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from "convex-helpers/react/cache";
import { useConvex } from "convex/react";
import { useEffect } from 'react';

import { Center, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { useIntersection } from "@mantine/hooks";

import { api } from "../../../convex/_generated/api";
import { writingRoles } from "../../../convex/shared_util";

import { useDetailStack, useOpenDetailId } from '../../components/DetailStack';
import { openAddEntryModal } from "../../components/EntryDialog";
import { entryItemSelector } from '../../components/EntryItem';
import EntryList from "../../components/EntryList";
import FloatingButton from "../../components/FloatingButton";
import { ENTRIES_PAGE_SIZE, PaginationStatus } from "../../components/GardenEntriesProvider";
import { JournalFilter, useJournalFilterState } from "../../components/JournalFilter";
import LayoutChooser, { useLayoutState } from "../../components/LayoutChooser";
import { openAddRichEntryModal } from "../../components/RichEntryDialog";
import useCurrentGarden from "../../hooks/useCurrentGarden";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  useGardenEntries,
  useGardenLocations,
  useGardenPlants,
} from "../../hooks/useGardenData";
import { waitForElement } from '../../lib/util';

import classes from "./panel.module.css";

/**
 * A filtered journal arrives whole, so there is no next page to ask for. Module-level so
 * that the sentinel effect below sees a stable identity rather than a new function on
 * every render.
 */
const noMoreToLoad = () => {};

export const Route = createFileRoute('/_app/g/$gardenToken/journal')({
  component: function JournalPane() {
    const currentGarden = useCurrentGarden();
    const convex = useConvex();
    // not `useFlags()`, which throws while the user is still loading
    const alphaTester = useCurrentUser()?.flags?.alphaTester ?? false;
    const { push: pushDetail } = useDetailStack();
    // the entry `?v=` has open, which the table layout highlights
    const openEntryId = useOpenDetailId("entry");

    const locations = useGardenLocations();
    const { activePlants } = useGardenPlants();
    const [filter, setFilter] = useJournalFilterState();

    const gardenEntries = useGardenEntries();

    // Skipped rather than branched around: hooks run unconditionally, and the unfiltered
    // subscription in `GardenEntriesProvider` is meant to stay alive across a filter, so
    // returning to Entire Garden costs no round trip and keeps the pages already loaded.
    const locationEntries = useQuery(
      api.garden.journal.listLocationEntries,
      currentGarden && filter?.type === "location"
        ? { gardenId: currentGarden._id, locationId: filter.id }
        : "skip"
    );
    const plantEntries = useQuery(
      api.garden.journal.listPlantEntries,
      currentGarden && filter?.type === "plant"
        ? { gardenId: currentGarden._id, plantId: filter.id }
        : "skip"
    );

    // One shape for `EntryList` whichever of the three queries is behind it. The filtered
    // ones are not paginated — the whole result arrives at once — so a filtered journal is
    // `Exhausted` from the moment it lands, which is also what keeps the infinite-scroll
    // sentinel below from firing while a filter is active.
    const filteredEntries = filter?.type === "location" ? locationEntries : plantEntries;
    const { entries, entriesStatus, loadMoreEntries } = filter === null
      ? gardenEntries
      : {
        entries: filteredEntries ?? [],
        entriesStatus: (
          filteredEntries === undefined ? "LoadingFirstPage" : "Exhausted"
        ) as PaginationStatus,
        loadMoreEntries: noMoreToLoad,
      };

    const [layout, setLayout] = useLayoutState("journal-pane-2", "timeline", ["table", "timeline"]);

    // Infinite scroll: request the next page well before the sentinel reaches the viewport.
    //
    // Options passed straight through to IntersectionObserver:
    //
    // - `rootMargin: "150% 0px"` — "<vertical> <horizontal>", so 150% is added to the top and
    //   bottom of the root box and 0 to the sides. Percentages resolve against the root's own
    //   dimensions, i.e. 1.5 viewport heights here. The sentinel therefore counts as
    //   intersecting while it's still ~1.5 screens below the fold, so the fetch starts that far
    //   ahead of the user and the next page is usually in hand before they reach the bottom.
    //   Tuning: larger = more speculative fetching (more entries loaded than read), smaller =
    //   more chance of the user hitting the `LoadingMore` spinner. The horizontal 0 matters —
    //   a percentage there would inflate the box sideways for no benefit.
    //
    // - No `root` — defaults to the viewport, which is correct because the journal scrolls in
    //   the window rather than inside a Mantine `ScrollArea`. If that ever changes, this needs
    //   the scroll container passed as `root`, or the margin will be measured against the wrong
    //   box and fire immediately.
    //
    // - No `threshold` — defaults to 0, i.e. fire as soon as any part of the sentinel enters the
    //   expanded root box, which is the earliest and therefore the trigger we want. It's also
    //   the only meaningful value here: the sentinel is an empty (zero-height) div whenever we
    //   aren't showing the loader, and IntersectionObserver reports a zero-area target's
    //   `intersectionRatio` as 1 or 0 rather than a fraction, so any threshold in (0, 1] would
    //   behave identically to 1 and none of them would be more precise.
    //
    // `useInViewport` is the more obvious-looking Mantine hook but accepts no options, so it
    // can't express the prefetch margin — it would only fire once the sentinel is actually
    // on screen, i.e. once the user has already hit the bottom.
    const { ref: sentinelRef, entry: sentinel } = useIntersection({ rootMargin: "150% 0px" });
    useEffect(() => {
      // Both deps are load-bearing. IntersectionObserver only fires on intersection
      // *changes*, so when a page loads without pushing the sentinel out of the margin,
      // `sentinel` doesn't update — it's the LoadingMore → CanLoadMore transition that
      // re-runs this effect against the still-intersecting sentinel to chain-load.
      if (sentinel?.isIntersecting && entriesStatus === "CanLoadMore") {
        loadMoreEntries(ENTRIES_PAGE_SIZE);
      }
    }, [sentinel, entriesStatus, loadMoreEntries]);

    if (!currentGarden) {
      return <></>;
    }

    // the window is the scroller, not the panel. `.panel` never gets a definite height —
    // it is a plain block child and `.contentLayer`'s `position: absolute; inset: 0` doesn't
    // constrain a block child — so it grows to fit its content and overflows the document
    // instead. Verified: panel.scrollHeight === panel.clientHeight. Two things follow, and
    // both flip the moment `.panel` gets a height: the sentinel's default
    // IntersectionObserver root (the viewport) is the right one, and the restoration id
    // below is storing 0. See docs/scrolling.md.
    //
    // The id is kept because it is what makes the panel's position addressable if it
    // ever does scroll — without one the router falls back to a generated nth-child
    // path, which any change to the tree invalidates. one id per tab, not one shared
    // id: positions are stored per key *and* per selector, so two panels on screen at
    // once would need to differ here
    return (
      <div className={classes.panel} data-scroll-restoration-id="journal-panel">
        {currentGarden && writingRoles.includes(currentGarden.role) && <FloatingButton
          label="Add entry"
          onClick={() => {
            const openModal = alphaTester ? openAddRichEntryModal : openAddEntryModal;
            openModal(convex, currentGarden._id, undefined, (entry) => {
              waitForElement(entryItemSelector(entry._id), (element) => {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
              });
            });
          }}
        />}
        <Stack gap="md">
          <Stack gap="xxs">
            <Group mt="md" justify="space-between" align="center">
              <Title order={2}>Journal</Title>
              {entries.length > 0 && (
                <LayoutChooser
                  options={["table", "timeline"]}
                  activeLayout={layout}
                  setLayout={setLayout}
                />)}
            </Group>
            <JournalFilter
              locations={locations}
              plants={activePlants}
              value={filter}
              onChange={setFilter}
            />
          </Stack>
          <Stack gap="xs">
            {entries.length > 0 && (
              <EntryList
                layout={layout}
                entries={entries}
                openId={openEntryId}
                onItemClick={(_, index) => pushDetail({ entries: entries.map((e) => ({ entry: e })), activeIndex: index })}
              />
            )}
            {/* A partial list no longer implies a small garden, so the onboarding tip
                needs exhaustion as well as a low count — and a filter can produce a short
                list in a garden that is not new at all, so it needs Entire Garden too. */}
            {filter === null && entriesStatus === "Exhausted" && entries.length <= 3 && (
              <Text c="dimmed" size="sm">
                Use journal entries to record anything you want to remember, like where you planted the tulips,
                when you started the tomato seedlings, or how great the flowers are looking right now!
              </Text>
            )}
            {/* Without this a filter that matches nothing leaves the pane blank under the
                chips, which reads as a failure rather than an answer. */}
            {filter !== null && entriesStatus === "Exhausted" && entries.length === 0 && (
              <Text c="dimmed" size="sm">
                No journal entries for this {filter.type} yet.
              </Text>
            )}
            {entriesStatus !== "Exhausted" && (
              <div ref={sentinelRef}>
                {/* Only seen if the user outruns the prefetch margin. */}
                {entriesStatus === "LoadingMore" && (
                  <Center><Loader size="sm" /></Center>
                )}
              </div>
            )}
          </Stack>
        </Stack>
      </div>
    );
  },
})

